/**
 * Assessment Summary Modal for Personal Gap Analysis
 * Displays CEPA-level interpretation and strategic guidance
 */
import React from 'react';
import PropTypes from 'prop-types';
import { X, FileBarChart } from 'lucide-react';

const AssessmentSummaryModal = ({
  show,
  onClose,
  loading,
  summaryData
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 border-b border-blue-700 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileBarChart size={32} />
              <div>
                <h2 className="text-2xl font-bold">Detailed Assessment Summary</h2>
                <p className="text-blue-100 text-sm">Exit Advisor Interpretation & Strategic Guidance</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Generating your detailed summary...</p>
          </div>
        ) : summaryData ? (
          <div className="p-6 space-y-6">
            {/* Overall Readiness Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {summaryData.readiness_level} Exit Readiness
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{summaryData.readiness_description}</p>
                </div>
                <div className="ml-6 text-right">
                  <div className="text-5xl font-bold text-blue-600">{Math.round(summaryData.overall_score)}%</div>
                  <div className="text-sm text-gray-600 mt-1">Overall Score</div>
                </div>
              </div>

              {/* Strengths and Weaknesses */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Top Strengths</h4>
                  <ul className="space-y-1">
                    {summaryData.strengths.map((strength, idx) => (
                      <li key={idx} className="text-sm text-gray-700">
                        <span className="font-medium">{strength.category}</span>: {Math.round(strength.score)}%
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border border-orange-200">
                  <h4 className="font-semibold text-orange-800 mb-2">Areas for Improvement</h4>
                  <ul className="space-y-1">
                    {summaryData.weaknesses.map((weakness, idx) => (
                      <li key={idx} className="text-sm text-gray-700">
                        <span className="font-medium">{weakness.category}</span>: {Math.round(weakness.score)}%
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* CEPA Interpretation Sections */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900">Exit Advisor Interpretation</h3>

              {/* Executive Summary */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-bold text-lg text-blue-900 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                  Executive Summary
                </h4>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {summaryData.cepa_interpretation.executive_summary}
                </p>
              </div>

              {/* Financial Readiness */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-bold text-lg text-green-900 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                  Financial Readiness
                </h4>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {summaryData.cepa_interpretation.financial_readiness}
                </p>
              </div>

              {/* Wellbeing Readiness */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-bold text-lg text-orange-900 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-orange-600 rounded-full mr-2"></span>
                  Wellbeing Readiness
                </h4>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {summaryData.cepa_interpretation.wellbeing_readiness}
                </p>
              </div>

              {/* Spiritual Readiness */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-bold text-lg text-purple-900 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mr-2"></span>
                  Spiritual Readiness
                </h4>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {summaryData.cepa_interpretation.spiritual_readiness}
                </p>
              </div>

              {/* Holistic Assessment */}
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <h4 className="font-bold text-lg text-indigo-900 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2"></span>
                  Holistic Assessment
                </h4>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {summaryData.cepa_interpretation.holistic_assessment}
                </p>
              </div>
            </div>

            {/* Strategic Recommendations */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900">Strategic Recommendations</h3>
              <div className="space-y-3">
                {summaryData.recommendations.map((rec, idx) => (
                  <div key={idx} className="bg-white border-l-4 border-blue-600 rounded-lg p-5 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-gray-900">{rec.title}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        rec.priority === 'Immediate' ? 'bg-red-100 text-red-800' :
                        rec.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {rec.priority} Priority
                      </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">{rec.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Next Steps</h3>
              <div className="space-y-4">
                {summaryData.next_steps.map((step, idx) => (
                  <div key={idx} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 mb-1">{step.action}</h4>
                      <p className="text-gray-700 text-sm mb-1">{step.description}</p>
                      <span className="text-xs font-semibold text-green-700">Timeframe: {step.timeframe}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-gray-600">No summary data available</p>
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition min-h-[44px]"
          >
            Close Summary
          </button>
        </div>
      </div>
    </div>
  );
};

AssessmentSummaryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  summaryData: PropTypes.object
};

export default AssessmentSummaryModal;
